import { useEffect, useRef } from "react";

const pillars = [
  {
    icon: "ri-compass-line",
    title: "Expertise terrain",
    description:
      "Plus de 10 ans d'expérience dans la coordination d'événements publics, gastronomiques, corporate et culturels en Suisse romande.",
  },
  {
    icon: "ri-equalizer-line",
    title: "Approche flexible",
    description:
      "Intervention sur des projets complets ou des missions ciblées selon les besoins de chaque organisation.",
  },
  {
    icon: "ri-team-line",
    title: "Réseau \u0026 partenaires",
    description:
      "Un solide réseau de fournisseurs, prestataires et partenaires de confiance mobilisés selon chaque projet.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Gestion opérationnelle",
    description:
      "Une présence terrain et une coordination précise pour assurer le bon déroulement de chaque événement.",
  },
];

const univers = [
  "Festivals \u0026 événements publics",
  "Événements gastronomiques",
  "Manifestations culturelles",
  "Événements corporate",
  "Lancements \u0026 inaugurations",
  "Concepts sur mesure",
];

export default function WhyChooseSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("opacity-100", "translate-y-0");
          el.classList.remove("opacity-0", "translate-y-8");

          cardsRef.current.forEach((card, i) => {
            if (card) {
              setTimeout(() => {
                card.classList.add("opacity-100", "translate-y-0");
                card.classList.remove("opacity-0", "translate-y-6");
              }, 120 * i);
            }
          });
        }
      },
      { threshold: 0.08 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="pourquoi"
      ref={sectionRef}
      className="py-16 md:py-24 relative opacity-0 translate-y-8 transition-all duration-700 bg-background-50"
    >

      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Title */}
        <h2 className="text-center font-playfair text-3xl md:text-4xl lg:text-[2.75rem] font-bold text-terracotta mb-6 md:mb-8 tracking-tight">
          POURQUOI NOUS CHOISIR
        </h2>

        {/* Intro */}
        <div className="text-center max-w-[54rem] mx-auto mb-10 md:mb-14">
          <p className="text-base sm:text-lg md:text-xl text-foreground-700 leading-[1.8]">
            Une approche pensée pour allier{" "}
            <strong className="text-foreground-900 font-medium">créativité</strong>,{" "}
            <strong className="text-foreground-900 font-medium">structure</strong>,{" "}
            <strong className="text-foreground-900 font-medium">
              fluidité opérationnelle
            </strong>{" "}
            et{" "}
            <strong className="text-foreground-900 font-medium">
              expérience publique
            </strong>
            , afin de concevoir des événements cohérents, marquants et
            parfaitement orchestrés.
          </p>
        </div>

        {/* 4 pillar cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-14 md:mb-20">
          {pillars.map((pillar, idx) => (
            <div
              key={pillar.title}
              ref={(node) => { cardsRef.current[idx] = node; }}
              className="group relative bg-white border border-background-200/50 rounded-2xl p-6 md:p-7 opacity-0 translate-y-6 transition-all duration-700 ease-out hover:border-primary-200/30 hover:shadow-[0_12px_40px_rgba(234,81,73,0.05)] hover:-translate-y-2 hover:scale-[1.02] shadow-[0_2px_12px_rgba(0,0,0,0.02)]"
              style={{ willChange: "transform" }}
            >
              {/* Soft coral glow on hover */}
              <div
                className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                style={{
                  background: "radial-gradient(ellipse at 50% 0%, rgba(139,59,59,0.04) 0%, transparent 60%)",
                }}
              />

              <div className="relative z-10 flex flex-col h-full">
                {/* Icon */}
                <div className="w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center mb-5 transition-all duration-500 group-hover:scale-110 bg-primary-50 border border-primary-100">
                  <i className={`${pillar.icon} w-5 h-5 md:w-6 md:h-6 flex items-center justify-center text-lg md:text-xl text-terracotta`} />
                </div>

                {/* Title */}
                <h3 className="text-[1.05rem] md:text-[1.15rem] font-sans font-semibold mb-3 tracking-tight leading-snug text-terracotta">
                  {pillar.title}
                </h3>

                {/* Divider */}
                <div className="w-8 h-px bg-primary-200/50 mb-3.5" />

                {/* Description */}
                <p className="text-[0.88rem] md:text-[0.92rem] text-foreground-800 leading-[1.75] flex-grow">
                  {pillar.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Univers d'intervention */}
        <div className="mb-14 md:mb-20">
          <div className="text-left mb-10 md:mb-12">
            <span className="text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-foreground-500 font-light">
              Univers d&apos;intervention
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 max-w-[58rem] mx-auto">
            {univers.map((tag) => (
              <div
                key={tag}
                className="group relative bg-white border border-background-200/50 rounded-2xl px-6 py-5 md:px-7 md:py-6 transition-all duration-500 hover:border-primary-200/30 hover:shadow-[0_8px_28px_rgba(234,81,73,0.04)] hover:-translate-y-1 flex items-center gap-3 shadow-[0_2px_12px_rgba(0,0,0,0.02)]"
              >
                <div className="w-2.5 h-2.5 rounded-full shrink-0 bg-[#8B3B3B]" />
                <h4 className="text-[0.95rem] md:text-[1.05rem] font-sans font-semibold text-foreground-800 leading-snug text-left">
                  {tag}
                </h4>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}